import { RRule } from 'rrule'
import { ValidationError } from 'yup'
import moment from 'moment'
import { isEmpty } from 'lodash'
import {
  IFormFields,
  IFormFieldsValidated,
  IScheduleRecord,
  IScheduleDataBasic,
  IScheduleData,
  IPublishScheduleFormErrors,
  FieldNames,
  ISelectOption,
  RecurrenceTypes,
  PeriodTypes,
  WeekdayTypes,
  MonthTypes,
  RecurrenceVariantTypes,
  IntradayRepeatTypes
} from '../types/publishSchedule'
import { createArray, capitalizeFirstLetter } from './common'
import { DATE_FORMATS } from '../config/date-formats'
import { safePost, safePut, schedulesHostUrl, safeGet, safeDelete } from './http'

const recurrenceToFrequencyMap = {
  [RecurrenceTypes.none]: RRule.DAILY,
  [RecurrenceTypes.daily]: RRule.DAILY,
  [RecurrenceTypes.weekly]: RRule.WEEKLY,
  [RecurrenceTypes.monthly]: RRule.MONTHLY,
  [RecurrenceTypes.yearly]: RRule.YEARLY,
  [RRule.DAILY]: RecurrenceTypes.daily,
  [RRule.WEEKLY]: RecurrenceTypes.weekly,
  [RRule.MONTHLY]: RecurrenceTypes.monthly,
  [RRule.YEARLY]: RecurrenceTypes.yearly,
}

const recurrenceOptions: Array<ISelectOption<RecurrenceTypes>> = [
  { value: RecurrenceTypes.none, label: 'None' },
  { value: RecurrenceTypes.daily, label: 'Daily' },
  { value: RecurrenceTypes.weekly, label: 'Weekly' },
  { value: RecurrenceTypes.monthly, label: 'Monthly' },
  { value: RecurrenceTypes.yearly, label: 'Yearly' },
]

const periodOptions: Array<ISelectOption<PeriodTypes>> = [
  { value: PeriodTypes.forever, label: 'Forever' },
  { value: PeriodTypes.untilDate, label: 'Until date' },
  { value: PeriodTypes.untilOccurrence, label: 'Until occurrence' },
]

const weekdayOptions: Array<ISelectOption<WeekdayTypes>> = [
  { value: WeekdayTypes.Monday, label: 'Monday' },
  { value: WeekdayTypes.Tuesday, label: 'Tuesday' },
  { value: WeekdayTypes.Wednesday, label: 'Wednesday' },
  { value: WeekdayTypes.Thursday, label: 'Thursday' },
  { value: WeekdayTypes.Friday, label: 'Friday' },
  { value: WeekdayTypes.Saturday, label: 'Saturday' },
  { value: WeekdayTypes.Sunday, label: 'Sunday' },
]

const monthOptions: Array<ISelectOption<MonthTypes>> = [
  { value: MonthTypes.January, label: 'January' },
  { value: MonthTypes.February, label: 'February' },
  { value: MonthTypes.March, label: 'March' },
  { value: MonthTypes.April, label: 'April' },
  { value: MonthTypes.May, label: 'May' },
  { value: MonthTypes.June, label: 'June' },
  { value: MonthTypes.July, label: 'July' },
  { value: MonthTypes.August, label: 'August' },
  { value: MonthTypes.September, label: 'September' },
  { value: MonthTypes.October, label: 'October' },
  { value: MonthTypes.November, label: 'November' },
  { value: MonthTypes.December, label: 'December' },
]

const weekdayCountOptions: Array<ISelectOption<number>> = [
  { value: 1, label: 'First' },
  { value: 2, label: 'Second' },
  { value: 3, label: 'Third' },
  { value: 4, label: 'Fourth' },
  { value: -1, label: 'Last' },
]

const intradayRepeatOptions: Array<ISelectOption<IntradayRepeatTypes>> = [
  { value: IntradayRepeatTypes.none, label: 'None' },
  { value: IntradayRepeatTypes.repeat, label: 'Repeat' },
  { value: IntradayRepeatTypes.repeatAllDay, label: 'Repeat all day' },
]

const intradayRepeatIntervalOptions: Array<ISelectOption<string>> = [
  ...createArray(11)
    .map((_: undefined, index: number) => ({ value: `${5 * (index + 1)}m`, label: `${5 * (index + 1)} Mins` })),
  ...createArray(12)
    .map((_: undefined, index: number) =>
      ({ value: `${index + 1}h`, label: `${index + 1} ${index + 1 === 1 ? 'Hour' : 'Hours'}` })),
]

export const dropdownOptions = {
  [FieldNames.recurrence]: recurrenceOptions,
  [FieldNames.period]: periodOptions,
  [FieldNames.recurrenceWeekdays]: weekdayOptions,
  [FieldNames.recurrenceWeekday]: weekdayOptions,
  [FieldNames.recurrenceWeekdayCount]: weekdayCountOptions,
  [FieldNames.recurrenceMonth]: monthOptions,
  [FieldNames.intradayRepeat]: intradayRepeatOptions,
  [FieldNames.intradayRepeatInterval]: intradayRepeatIntervalOptions,
}

export const startAdornments = {
  [RecurrenceTypes.daily]: 'Every',
  [RecurrenceTypes.weekly]: 'Every',
  [RecurrenceTypes.monthly]: 'Of every',
}

export const endAdornments = {
  [RecurrenceTypes.daily]: 'days',
  [RecurrenceTypes.weekly]: 'weeks',
  [RecurrenceTypes.monthly]: 'month',
}

export const dateTimeFieldLabels = {
  [FieldNames.startTime]: 'Start time',
  [FieldNames.endTime]: 'End time',
  [FieldNames.intradayRepeatUntil]: 'Until',
}

export const dateTimeFieldPlaceholders = {
  [FieldNames.startTime]: 'Select start time',
  [FieldNames.endTime]: 'Select end time',
  [FieldNames.intradayRepeatUntil]: 'Select until what time',
}

export const schedulesUrl = `${schedulesHostUrl}/schedules`

export const createSchedule = (
  formFieldsData: IFormFieldsValidated,
  group: string,
  packId: string
): Promise<IScheduleRecord> => {
  const scheduleData: IScheduleData = {
    ...formStateToScheduleData(formFieldsData),
    group,
    type: 'Publish',
    index: true,
  }
  return safePost(`${schedulesUrl}/${packId}`, scheduleData)
}

export const updateSchedule = (
  previousScheduleData: IScheduleRecord,
  formFieldsData: IFormFieldsValidated,
  packId: string
): Promise<IScheduleRecord> => {
  const scheduleData: IScheduleData = {
    ...previousScheduleData,
    ...formStateToScheduleData(formFieldsData),
  }
  return safePut(`${schedulesUrl}/${packId}/${previousScheduleData.scheduleId}`, scheduleData)
}

export const deleteSchedule = (
  scheduleId: string,
  packId: string
) => safeDelete(`${schedulesUrl}/${packId}/${scheduleId}`)

// TODO: reuse the backend response type @BE
export const getSchedules = (
  packId?: string
): Promise<IScheduleRecord[]> =>
  safeGet(`${schedulesUrl}/${packId}?type=publish`)

export const formStateToScheduleData = (formFieldsData: IFormFieldsValidated): IScheduleDataBasic => ({
  startTime: convertAndFormatDate(formFieldsData.startTime),
  recurring: formFieldsData.recurrence !== RecurrenceTypes.none,
  intradayRepeat: formFieldsData.intradayRepeat !== IntradayRepeatTypes.none,
  intradayRepeatInterval: formFieldsData.intradayRepeat !== IntradayRepeatTypes.none
    ? formFieldsData.intradayRepeatInterval
    : null,
  intradayRepeatUntil: formFieldsData.intradayRepeat === IntradayRepeatTypes.repeat
    ? moment.utc(formFieldsData.intradayRepeatUntil)
      .format(DATE_FORMATS.SCHEDULE_REQUEST_TIME_ONLY)
    : null,
  rrule: formFieldsData.recurrence !== RecurrenceTypes.none ? fieldsDataToRRule(formFieldsData).toString() : null,
})

export const scheduleDataToFormState = ({
  startTime,
  recurring,
  rrule,
  intradayRepeat,
  intradayRepeatInterval,
  intradayRepeatUntil,
}: IScheduleRecord): Partial<IFormFields> => {
  const rruleOptions = recurring && !!rrule && RRule.fromString(rrule).options
  return {
    startTime: moment(startTime).toDate(),
    ...(!!rruleOptions ? {
      recurrence: rruleOptions.freq !== undefined
        ? recurrenceToFrequencyMap[rruleOptions.freq]
        : RecurrenceTypes.none,
      recurrenceVariant: !isEmpty(rruleOptions.byweekday) && !isEmpty(rruleOptions.bysetpos)
        ? RecurrenceVariantTypes.dayByName
        : RecurrenceVariantTypes.dayByNumber,
      recurrenceDayNumber: !isEmpty(rruleOptions.bymonthday) ? rruleOptions.bymonthday[0] : 1,
      recurrenceWeekday: !isEmpty(rruleOptions.byweekday) ? rruleOptions.byweekday[0] : 0,
      recurrenceWeekdayCount: !isEmpty(rruleOptions.bysetpos) ? rruleOptions.bysetpos[0] : 1,
      recurrenceWeekdays: !isEmpty(rruleOptions.byweekday) ? rruleOptions.byweekday : [0],
      recurrenceMonth: !isEmpty(rruleOptions.bymonth) ? rruleOptions.bymonth[0] : 1,
      period: !!rruleOptions.until
        ? PeriodTypes.untilDate
        : !!rruleOptions.count
          ? PeriodTypes.untilOccurrence
          : PeriodTypes.forever,
      everyNthPeriod: !!rruleOptions.interval ? rruleOptions.interval : 1,
      occurrences: !!rruleOptions.count ? rruleOptions.count : 1,
      endTime: !!rruleOptions.until ? rruleOptions.until : null,
    } : {}),
    intradayRepeat: !intradayRepeat
      ? IntradayRepeatTypes.none
      : !!intradayRepeatUntil
        ? IntradayRepeatTypes.repeat
        : IntradayRepeatTypes.repeatAllDay,
    intradayRepeatInterval: intradayRepeatInterval || '5m',
    intradayRepeatUntil: !!intradayRepeatUntil
      ? moment(`${moment().format('YYYY-MM-DDT')}${intradayRepeatUntil}`).toDate()
      : null,
  }
}

export const fieldsDataToRRule = ({
  startTime,
  recurrence,
  everyNthPeriod,
  period,
  occurrences,
  recurrenceWeekdays,
  recurrenceVariant,
  recurrenceDayNumber,
  recurrenceWeekdayCount,
  recurrenceWeekday,
  recurrenceMonth,
  endTime,
}: IFormFieldsValidated): RRule => {
  const monthlyOrYearlyRecurrence = recurrence === RecurrenceTypes.monthly || recurrence === RecurrenceTypes.yearly
  return new RRule({
    dtstart: startTime,
    freq: recurrenceToFrequencyMap[recurrence],
    interval: everyNthPeriod,
    count: period === PeriodTypes.untilOccurrence ? occurrences : undefined,
    byweekday: recurrence === RecurrenceTypes.weekly && recurrenceWeekdays.length > 0
      ? recurrenceWeekdays.map((weekdayNumber: number) => weekdayNumber)
      : monthlyOrYearlyRecurrence && recurrenceVariant === RecurrenceVariantTypes.dayByName
        ? recurrenceWeekday
        : undefined,
    bymonthday: monthlyOrYearlyRecurrence && recurrenceVariant === RecurrenceVariantTypes.dayByNumber
      ? recurrenceDayNumber
      : undefined,
    bymonth: recurrence === RecurrenceTypes.yearly ? recurrenceMonth : undefined,
    bysetpos: monthlyOrYearlyRecurrence && recurrenceVariant === RecurrenceVariantTypes.dayByName
      ? recurrenceWeekdayCount
      : undefined,
    until: period === PeriodTypes.untilDate ? endTime : undefined,
  })
}

export const convertAndFormatDate = (date?: Date | null) =>
  moment.utc(date || new Date()).format(DATE_FORMATS.SCHEDULE_REQUEST)

export const validationErrorReducer = (
  errors: IPublishScheduleFormErrors,
  { path, message }: ValidationError
): IPublishScheduleFormErrors => ({
  ...errors,
  [path]: message,
})

export const getMinEndDate = ({
  recurrenceVariant,
  recurrenceWeekday,
  recurrenceWeekdays,
  recurrenceWeekdayCount,
  recurrenceDayNumber,
  recurrenceMonth,
  startTime,
  everyNthPeriod,
  recurrence,
  intradayRepeat,
  intradayRepeatInterval,
}: IFormFields): Date => {
  const defaultStartTime = moment(startTime || moment().endOf('minute')).toDate()
  const scheduleInstances = fieldsDataToRRule({
    startTime: defaultStartTime,
    recurrence,
    recurrenceVariant,
    recurrenceWeekdays,
    recurrenceWeekday,
    recurrenceWeekdayCount,
    recurrenceDayNumber: recurrenceDayNumber
      ? recurrenceDayNumber
      : defaultStartTime.getDate(),
    recurrenceMonth,
    intradayRepeat,
    intradayRepeatInterval,
    everyNthPeriod: everyNthPeriod || 1,
    period: PeriodTypes.untilOccurrence,
    occurrences: 2,
  }).all()
  return moment(scheduleInstances.length === 0 ? defaultStartTime : scheduleInstances[0]).add(1, 'minutes').toDate()
}

export const getValueIfPositiveInteger = (value: string): number | null =>
  parseInt(value, 10) > 0 ? parseInt(value, 10) : null

export const intradayRepeatUntilToText = (time: string): string =>
  moment(`${moment().format('YYYY-MM-DDT')}${time}`).format(DATE_FORMATS.TIME_ONLY)

export const startTimeToText = (date: string): string =>
  `${moment(date).format(DATE_FORMATS.SHORT)} at ${moment(date).format(DATE_FORMATS.TIME_ONLY)}`

export const intradayRepeatToText = (
  intradayRepeatInterval: string,
  intradayRepeatUntil: string | null
): string => {
  const until = intradayRepeatUntil ? `until ${intradayRepeatUntilToText(intradayRepeatUntil)}` : ''
  return `Repeat every ${intradayRepeatInterval}(s) ${until}`
}

export const rruleToText = (rrule: string, startTime: string): string => {
  const insertion = `from ${startTimeToText(startTime)}`
  const forPosition = rrule.indexOf('for')
  const untilPosition = rrule.indexOf('until')
  return forPosition !== -1
    ? `${rrule.substring(0, forPosition)} ${insertion} ${rrule.substring(forPosition)}`
    : untilPosition !== -1
      ? `${rrule.substring(0, untilPosition)} ${insertion} ${rrule.substring(untilPosition)}`
      : `${rrule} from ${startTimeToText(startTime)}`
}

export const renderScheduleDateTime = ({
  intradayRepeat,
  rrule,
  startTime,
  intradayRepeatInterval,
  intradayRepeatUntil,
  recurring,
}: IScheduleRecord): string => {
  if (recurring) {
    return intradayRepeat
      ? `${intradayRepeatInterval && intradayRepeatToText(intradayRepeatInterval, intradayRepeatUntil)} of ${rrule && rruleToText(RRule.fromString(rrule).toText(), startTime)}`
      : `${rrule && rruleToText(capitalizeFirstLetter(RRule.fromString(rrule).toText()), startTime)}`
  } else {
    return intradayRepeat
      ? `${intradayRepeatInterval && intradayRepeatToText(intradayRepeatInterval, intradayRepeatUntil)}`
      : `${startTimeToText(startTime)}`
  }
}
